import { validateOptionalEditorText } from "./editorValidation.js";

export type AnnouncementComposerInput = {
  announcementId: string;
  title: string;
  titleAr: string;
  body: string;
  bodyAr: string;
  expiresAt: string;
  priority: "normal" | "urgent";
  pushNotificationEnabled?: boolean;
};

export type AnnouncementComposerDraft = {
  pushNotificationEnabled: boolean;
  announcementId: string;
  title: string;
  titleAr: string;
  body: string;
  bodyAr: string;
  audience: "connected_people";
  expiresAt: Date | null;
  priority: "normal" | "urgent";
  status: "published";
  visibility: "connected";
  source: "experience_six";
};

export type AnnouncementComposerResult =
  | {
      ok: true;
      draft: AnnouncementComposerDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type AnnouncementComposerFixture = {
  body: string;
  bodyAr: string;
  id: string;
  priority: "normal" | "urgent";
  title: string;
  titleAr: string;
};

const titleLimit = 140;
const bodyLimit = 1200;

export function buildAnnouncementComposerInputFromFixture(
  fixture: AnnouncementComposerFixture,
): AnnouncementComposerInput {
  return {
    announcementId: `${fixture.id}-draft`,
    body: fixture.body,
    bodyAr: fixture.bodyAr,
    expiresAt: "",
    priority: fixture.priority,
    title: fixture.title,
    titleAr: fixture.titleAr,
  };
}

export function buildDefaultAnnouncementComposerInput(
  organizationId: string,
): AnnouncementComposerInput {
  return {
    announcementId: `community-announcement-${organizationId}`,
    body: "A community update for connected people.",
    bodyAr: "تحديث مجتمعي للأشخاص المتصلين.",
    expiresAt: "",
    priority: "normal",
    title: "Community update",
    titleAr: "تحديث المجتمع",
  };
}

export function buildEmptyAnnouncementComposerInput(): AnnouncementComposerInput {
  return {
    announcementId: "",
    body: "",
    bodyAr: "",
    expiresAt: "",
    priority: "normal",
    title: "",
    titleAr: "",
  };
}

export function evaluateAnnouncementComposerInput(
  input: AnnouncementComposerInput,
): AnnouncementComposerResult {
  const announcementId = normalizeAnnouncementDocumentId(input.announcementId);
  const title = input.title.trim();
  const titleAr = input.titleAr.trim();
  const body = input.body.trim();
  const bodyAr = input.bodyAr.trim();
  const expiresAt = parseOptionalAnnouncementExpiration(input.expiresAt);
  const errors: string[] = [];

  validateRequiredText("Announcement id", announcementId, 80, errors);
  validateRequiredText("English title", title, titleLimit, errors);
  validateOptionalEditorText("Arabic title", titleAr, titleLimit, errors);
  validateRequiredText("English body", body, bodyLimit, errors);
  validateOptionalEditorText("Arabic body", bodyAr, bodyLimit, errors);

  if (input.priority !== "normal" && input.priority !== "urgent") {
    errors.push("Priority must be normal or urgent.");
  }

  if (expiresAt === undefined) {
    errors.push("Expiration date must be a valid date and time.");
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  const draftExpiresAt = expiresAt ?? null;

  return {
    ok: true,
    draft: {
      pushNotificationEnabled: input.pushNotificationEnabled === true,
      announcementId,
      audience: "connected_people",
      body,
      bodyAr,
      expiresAt: draftExpiresAt,
      priority: input.priority,
      source: "experience_six",
      status: "published",
      title,
      titleAr,
      visibility: "connected",
    },
  };
}

export function normalizeAnnouncementDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function parseOptionalAnnouncementExpiration(value: string): Date | null | undefined {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const parsed = new Date(trimmed);

  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed;
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}
